import { FC, ReactNode, Suspense, lazy, useEffect, useRef } from 'react';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter, withRouter } from 'react-router-dom';
import CountrySpecific from './common/country-specific/country-specific';
import ErrorBoundary from './common/error-boundary/error-boundary';
import FeedbackForm from './common/feedback-form/feedback-form';
import InteractiveFeatureAnnouncementPopup from './common/interactive/interactive-feature-announcement-popup/interactive-feature-announcement-popup';
import InteractiveQuizAnnouncementPopup from './common/interactive/interactive-quiz-announcement-popup/interactive-quiz-announcement-popup';
import LmpLazy from './common/lmp-lazy/lmp-lazy';
import NewWorldsLaunchModal from './common/new-worlds-launch-modal/new-worlds-launch-modal';
import ScrollToTop from './common/scroll-to-top/scroll-to-top';
import SmartAppBanner from './common/smart-banner/smart-banner';
import Routes from './constants/routes';
import InteractivePosthogExperimentsContext from './contexts/interactive-posthog-experiments-context';
import { ToastProvider } from './contexts/toast-notification-context';
import useBraze from './hooks/use-braze';
import './i18n';
import { world } from './languages';
import Header from './layouts/main/header/header';
import { getLocation } from './reducers/geotargeting';
import { initWebsocket } from './reducers/websocket';
import routes from './routes';
import usePosthogExperiments from './routes/new-interactive/hooks/use-posthog-experiments';
import store from './store';
import documentLang from './utils/document-lang';
import { domainLinkCreator } from './utils/domain-manager';
import enhancedEcommerce from './utils/enhanced-ecommerce';
import externalScriptsManager from './utils/external-scripts-manager';
import initGlobalABTest from './utils/global-ab-test/global-ab-test';
import isPrerender from './utils/is-prerender';
import isUITest from './utils/is-ui-test';
import lastVisitedWorld from './utils/last-visited-world';
import { identifyUserForPosthog, initPostHog } from './utils/posthog-helpers';
import renderPermissions from './utils/render-permissions';
import schedule from './utils/sale/schedule';
import trackPageView from './utils/track-page-view';
import './index.scss';

// retry

const PersonalizationModal = lazy(
    () => import('./common/personalization/components/personalization-modal/pesonalization-modal')
);
const AddToCartModal = lazy(() => import('./common/add-to-cart-modal/add-to-cart-modal'));
const Footer = lazy(() => import(/* webpackChunkName: "footer" */ './layouts/main/footer/footer'));

schedule.set();
documentLang.set();
lastVisitedWorld.set(world);

window.locationReload = (path: string) => {
    window.location.href = domainLinkCreator(path);
};

getLocation()(store.dispatch);
initWebsocket()(store.dispatch);
initGlobalABTest('optimize.activate_carousel', 'imageCarousel');

const Main = withRouter((props) => {
    const permissions = renderPermissions();
    const headerProps = { headerClass: permissions.headerClass };
    const { location } = props;
    const fullURL = location.pathname + location.search;
    const prevUrl = useRef(window.location.pathname);

    useEffect(() => {
        setTimeout(() => trackPageView(), 1);
        enhancedEcommerce.tracker.impressionView.resetImpressionsHistory();
    }, [fullURL]);

    useEffect(() => {
        const { pathname, search } = window.location;
        const isMaterial = pathname.indexOf(Routes.MATERIAL.path.slice(0, Routes.MATERIAL.path.indexOf(':id'))) > -1;

        if (isMaterial) {
            const id = pathname.split('/')[3];

            window.sessionStorage.setItem(`material_${id}`, prevUrl.current);
        }

        prevUrl.current = pathname + search;
    }, [window.location.pathname, location.search]);

    return (
        <div className='App'>
            {permissions.header && (
                <ErrorBoundary>
                    <Header {...headerProps} />
                </ErrorBoundary>
            )}
            <div className='wrap position-relative'>{routes}</div>
            {permissions.footer && (
                <ErrorBoundary>
                    <LmpLazy
                        className='mh-300'
                        content={
                            <Suspense fallback={<div />}>
                                <Footer footerClass={permissions.footerClass || ''} />
                            </Suspense>
                        }
                        offset={99}
                    />
                </ErrorBoundary>
            )}
        </div>
    );
});

const ContentWrapper: FC<{ children: ReactNode }> = ({ children }) => {
    const cookieConsent = useSelector((state: RootState) => state.main.cookieConsent);
    const posthogExperiments = usePosthogExperiments();

    useBraze();

    const userId = window.storage.getItem('userId');

    useEffect(() => {
        initPostHog();
    }, []);

    useEffect(() => {
        if (userId) {
            identifyUserForPosthog(userId);
        }
    }, [userId]);

    useEffect(() => {
        if (cookieConsent?.services?.length || isUITest() || isPrerender()) {
            externalScriptsManager.load(cookieConsent.services);
        }
    }, [cookieConsent]);

    return (
        <InteractivePosthogExperimentsContext.Provider value={{ ...posthogExperiments }}>
            {children}
        </InteractivePosthogExperimentsContext.Provider>
    );
};

const permissions = renderPermissions();

const App = () => {
    return (
        <>
            {permissions.appBanner && <SmartAppBanner />}
            <InteractiveFeatureAnnouncementPopup />

            <Provider store={store}>
                <CountrySpecific allowed='de'>
                    <InteractiveQuizAnnouncementPopup />
                </CountrySpecific>
                <FeedbackForm />
                <NewWorldsLaunchModal />
                {/*@ts-ignore*/}
                <BrowserRouter>
                    <ContentWrapper>
                        <>
                            <ToastProvider>
                                <Main />
                            </ToastProvider>
                            <ScrollToTop />
                            <Suspense fallback={<></>}>
                                <PersonalizationModal />
                            </Suspense>
                            <Suspense fallback={<></>}>
                                <AddToCartModal />
                            </Suspense>
                        </>
                    </ContentWrapper>
                </BrowserRouter>
            </Provider>
        </>
    );
};

export default App;
